import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { useMatter } from '@/hooks/useMatter';
import type { CreatePoliceReport } from '@/services/api/generated/policeReportsService';
import { usePoliceReportsCreateMutation } from '@/services/api/police';

import { PoliceReportForm } from './forms/PoliceReportForm';

export const CreatePoliceReportPage = () => {
  const navigate = useNavigate();
  const { matterId } = useMatter();
  const [createReport, { isLoading }] = usePoliceReportsCreateMutation();

  const formData: Partial<CreatePoliceReport> = {
    matter_id: matterId,
    attestation_status: 'unclear',
  };

  const handleBack = () => {
    navigate(`/matters/${matterId}/police`);
  };

  const handleSubmit = async (data: Partial<CreatePoliceReport>) => {
    if (!matterId) {
      toast.error('Matter ID is required');
      return;
    }

    try {
      const reportData = {
        ...data,
        matter_id: matterId,
      } as CreatePoliceReport;

      const result = await createReport({
        matterId,
        createPoliceReport: reportData,
      }).unwrap();

      toast.success('Police report created successfully');
      navigate(`/matters/${matterId}/police/${result.id}`);
    } catch (error) {
      toast.error('Failed to create police report');
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-4 py-4">
            <Button
              variant="outline"
              size="sm"
              onClick={handleBack}
              className="flex items-center space-x-1"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              <span>Back to Police Reports</span>
            </Button>
            <h1 className="text-2xl font-bold text-gray-900">
              Add Police Report
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-sm rounded-lg">
          <div className="p-6">
            <PoliceReportForm
              initialData={formData}
              onSubmit={handleSubmit}
              onCancel={handleBack}
              isLoading={isLoading}
              submitButtonText="Create Report"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
